// src/hooks/usePortfolio.js
import { useMemo, useCallback } from "react";
import { STOCK_UNIVERSE } from "../data/stocks";
import { calcPortfolioMetrics, optimizeWeights } from "../utils/finance";
import { supabase, hasSupabase } from "../lib/supabaseClient";
import { saveSnapshot, getCurrentPortfolioValue } from "../lib/portfolioSnapshots";

async function fireHoldingSnapshot(triggerType, notes) {
  if (!hasSupabase) return;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const val = await getCurrentPortfolioValue(user.id);
    if (val != null) await saveSnapshot(user.id, val, triggerType, notes);
  } catch {}
}

export function usePortfolio({
  holdings, setHoldings,
  riskTolerance, setRiskTolerance,
  tier = "free",
  onOpenBilling = null,
}) {
  const totalWeight = useMemo(
    () => parseFloat(holdings.reduce((s, h) => s + h.weight, 0).toFixed(1)),
    [holdings],
  );

  const metrics = useMemo(() => calcPortfolioMetrics(holdings), [holdings]);

  const sectorBreakdown = useMemo(() => {
    const map = {};
    holdings.forEach(h => { map[h.sector] = parseFloat(((map[h.sector] || 0) + h.weight).toFixed(1)); });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [holdings]);

  const addStock = useCallback((stock) => {
    const full = typeof stock === "string"
      ? { ticker: stock, name: stock, sector: "Unknown", pe: null, pb: null, evEbitda: null, beta: 1, expectedReturn: 10, volatility: 20, dividend: 0 }
      : stock;
    const canAdd = !(tier === "free" && holdings.length >= 5) && !holdings.find(h => h.ticker === full.ticker);
    setHoldings(prev => {
      if (tier === "free" && prev.length >= 5) { onOpenBilling?.(); return prev; }
      if (prev.find(h => h.ticker === full.ticker)) return prev;
      return [...prev, { ...full, weight: 10, shares: null, costBasis: null }];
    });
    if (canAdd) fireHoldingSnapshot('holding_added', `Added ${full.ticker}`);
  }, [setHoldings, tier, onOpenBilling, holdings]);

  const removeStock = useCallback((ticker) => {
    setHoldings(prev => prev.filter(h => h.ticker !== ticker));
    fireHoldingSnapshot('holding_removed', `Removed ${ticker}`);
  }, [setHoldings]);

  const updateWeight = useCallback((ticker, raw) => {
    const val = Math.max(0, Math.min(100, parseFloat(raw) || 0));
    setHoldings(prev => prev.map(h => h.ticker === ticker ? { ...h, weight: val } : h));
  }, [setHoldings]);

  const equalWeight = useCallback(() => {
    setHoldings(prev => {
      const w = parseFloat((100 / prev.length).toFixed(1));
      return prev.map(h => ({ ...h, weight: w }));
    });
  }, [setHoldings]);

  const optimize = useCallback(() => {
    setHoldings(prev => optimizeWeights(prev, riskTolerance));
  }, [setHoldings, riskTolerance]);

  const addCustomStock = useCallback((stock) => {
    const canAdd = !(tier === "free" && holdings.length >= 5) && !holdings.find(h => h.ticker === stock.ticker);
    setHoldings(prev => {
      if (tier === "free" && prev.length >= 5) { onOpenBilling?.(); return prev; }
      if (prev.find(h => h.ticker === stock.ticker)) return prev;
      return [...prev, { ...stock, weight: 10, shares: null, costBasis: null }];
    });
    if (canAdd) fireHoldingSnapshot('holding_added', `Added ${stock.ticker}`);
  }, [setHoldings, tier, onOpenBilling, holdings]);

  // Used by CSV import — bypasses the default weight:10 override
  const importHoldings = useCallback((rows, mode = "replace") => {
    setHoldings(prev => {
      const base = mode === "replace" ? [] : [...prev];
      const next = [...base];
      rows.forEach(({ ticker, weight, shares, costBasis }) => {
        if (!next.find(h => h.ticker === ticker)) {
          next.push({
            ticker,
            name: ticker,
            sector: STOCK_UNIVERSE.find(s => s.ticker === ticker)?.sector ?? "Unknown",
            weight: parseFloat(weight) || 0,
            shares: shares ?? null,
            costBasis: costBasis ?? null,
          });
        }
      });
      if (tier === "free" && next.length > 5) {
        onOpenBilling?.();
        return next.slice(0, 5);
      }
      return next;
    });
  }, [setHoldings, tier, onOpenBilling]);

  return {
    holdings, totalWeight, metrics, sectorBreakdown,
    riskTolerance, setRiskTolerance,
    addStock, removeStock, updateWeight, equalWeight, optimize, addCustomStock,
    importHoldings,
  };
}
